import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import {
  CheckoutPageProvider,
  GeneralSearchProvider,
  RemoteDataProviders,
} from "../../contexts";
import { useAuth, useHasAccessClaim } from "../../hooks";
import {
  HostedPaymentLinks,
  IFramePaymentRedirectHandler,
  NotFoundPages,
} from "../../pages";
import { DataLoadingView } from "../DataLoadingView";

import { ProtectedRouter } from "../ProtectedRouter";
import { AuthLayout } from "./AuthLayout";
import { OnboardingLayout } from "./OnboardingLayout";
import { NoDepartmentCreateAccountLayout } from "./NoDepartmentCreateAccountLayout";
import { userAccessPolicyConstants } from "../../constants";

// import { View } from "./View";

// lazy load the rest of the app.
// this speeds up the auth section (which contains unauthenticated <HostedPaymentLinks />) required for payment by customers
const View = lazy(() => import("./View"));

export const AppLayout = () => {
  const { is_logged_in, staff_departments } = useAuth();

  const noStaffDepartments = staff_departments.length === 0;

  const userIsOwner = useHasAccessClaim(userAccessPolicyConstants.owner);
  const userIsDeveloper = useHasAccessClaim(
    userAccessPolicyConstants.developer
  );

  const hasDeveloperAccess = userIsDeveloper || userIsOwner;

  return (
    <ProtectedRouter
      isAuthenticated={is_logged_in}
      unprotectedRoutes={<Route path="*" element={<AuthLayout />} />}
      protectedRoutes={
        // NOTE: <RemoteDataProviders /> were added here b/c auth is required for accessing the data
        <Route
          path="*"
          element={
            noStaffDepartments ? (
              <NoDepartmentCreateAccountLayout />
            ) : (
              <RemoteDataProviders>
                <CheckoutPageProvider>
                  <GeneralSearchProvider>
                    <Routes>
                      <Route
                        path="/onboarding/*"
                        element={
                          <>
                            <OnboardingLayout />
                          </>
                        }
                      />

                      {/* TODO: move hosted links to separate application. This is a temp measure for speed */}
                      <Route
                        path="/hosted-payment-links/*"
                        element={<HostedPaymentLinks isPreviewMode={false} />}
                      />

                      <Route
                        path="/iframe-payment-redirect-handler/*"
                        element={<IFramePaymentRedirectHandler />}
                      />

                      {/* NOT FOUND PAGES */}
                      <Route
                        path="/page-not-found/*"
                        element={<NotFoundPages />}
                      />

                      <Route
                        path="/hosted-link-expired/*"
                        element={
                          <NotFoundPages
                            pageType="payment-link"
                            message="This payment link has either expired or does not exist"
                          />
                        }
                      />

                      <Route
                        path="*"
                        element={
                          <Suspense
                            fallback={
                              <DataLoadingView message="loading page" />
                            }
                          >
                            <View userHasDeveloperAccess={hasDeveloperAccess} />
                          </Suspense>
                        }
                      />
                    </Routes>
                  </GeneralSearchProvider>
                </CheckoutPageProvider>
              </RemoteDataProviders>
            )
          }
        />
      }
    />
  );
};
